import { toCurrencyTruncated } from '@/utilities/formatting';
import { CompetitorSkeleton } from '@/pages/CompanyReport/components/SimilarCompanies/Components/Competitor/CompetitorSkeleton';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import { CompanyLogo } from './CompanyLogo';
import styled, { css } from 'styled-components';

import { getCompanyStockUrl } from '../utils';
import { useCompetitors } from '../hooks/useCompetitors';
import { ROUTE_STOCKS_MARKET_INDUSTRY } from '@/constants/routes';
import { Button } from '@simplywallst/ui-core/next';
import { getPath } from '@/router/utils';

const CompetitorWrapper = styled('article')<{ disabled?: boolean }>`
  text-align: center;
  border-radius: ${({ theme }) => theme.x1spacePx}px;
  ${({ disabled }) => !disabled && 'cursor: pointer;'}

  ${({ disabled }) =>
    !disabled &&
    css`
      &:hover {
        background-color: var(--s-color-surface-2);
      }
    `}
`;

function Competitor({
  index,
  companyId,
  companyName,
  uniqueSymbol,
  marketCap,
  companyUrl,
  logo,
}: {
  index: number;
  companyId: string;
  uniqueSymbol: string;
  companyName: string;
  companyUrl: string;
  marketCap: string;
  logo: { url: string; tickerSymbol: string; hasLogo: boolean };
}) {
  const { trackEvent } = useTracking();

  return (
    <CompetitorWrapper data-cy-id={`competitor-${index}`}>
      <RouterLink
        inheritColor
        noUnderline
        to={companyUrl}
        onClick={() => {
          trackEvent({
            action: 'click',
            modifier: 'competitors',
            index,
            uniqueSymbol,
            companyId,
          });
        }}
      >
        <div className="p-4 pt-5">
          <div className="grid place-content-center shadow-sm">
            <CompanyLogo
              website={logo.url}
              tickerSymbol={logo.tickerSymbol}
              size={75}
              hasLogo={logo.hasLogo}
            />
          </div>
          <div className="flex flex-col gap-y-1.5 pt-2">
            <h3 className="m-0 truncate text-sm">{companyName}</h3>
            <div className="text-xs">
              <div className="uppercase">
                <span className="sr-only">Symbol: </span>
                <span>{uniqueSymbol}</span>
              </div>
              <div className="truncate opacity-50">
                <span className="sr-only">Market cap: </span>
                <span>{marketCap}</span>
              </div>
            </div>
          </div>
        </div>
      </RouterLink>
    </CompetitorWrapper>
  );
}

export const Competitors = () => {
  const {
    companyId,
    uniqueSymbol,
    listingCurrencyISO,
    primaryIndustry,
    competitors,
    status,
    exchangeCountryISO,
  } = useCompetitors();

  const { trackEvent } = useTracking();

  const targetStocksPage = {
    type: ROUTE_STOCKS_MARKET_INDUSTRY,
    payload: {
      market: exchangeCountryISO,
      industry: primaryIndustry.slug,
      sortBy: 'market-cap-large',
    },
  };

  return (
    <div className="scrollbar overflow-x-auto overflow-y-hidden @container">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(30ch,1fr))]">
        {status === 'pending' ? (
          <>
            <CompetitorSkeleton />
            <CompetitorSkeleton />
            <CompetitorSkeleton />
            <CompetitorSkeleton />
          </>
        ) : (
          competitors.map((n, i) => {
            if (n.companyId === null) return null;
            const marketCap = n.marketCap;

            return (
              <Competitor
                key={`competitor-holding-${n.companyId + i}`}
                index={i}
                companyId={n.companyId}
                companyName={n.shortName}
                uniqueSymbol={n.uniqueSymbol}
                marketCap={
                  marketCap
                    ? toCurrencyTruncated(
                        marketCap,
                        1,
                        listingCurrencyISO ?? undefined
                      )
                    : 'n/a'
                }
                companyUrl={getCompanyStockUrl(n.uniqueSymbol)}
                logo={{
                  url: n.website ?? '',
                  tickerSymbol: n.tickerSymbol ?? '',
                  hasLogo: n.hasLogo ?? false,
                }}
              />
            );
          })
        )}
      </div>
      <div className="w-max">
        <Button
          href={getPath(targetStocksPage).toLowerCase()}
          variant="secondary"
          className="mt-x2 w-full"
          onClick={() => {
            trackEvent({
              action: 'click',
              modifier: 'competitors',
              uniqueSymbol,
              companyId,
            });
          }}
        >
          View more competitors
        </Button>
      </div>
    </div>
  );
};
